import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, Subject } from 'rxjs';

import { HeaderComponent } from '../../components/header/header.component';
import { BookingState, BookingStateService } from '../../services';
import { HostEvent, HostEventService } from '../../services/host-event.service';
import { resolveHostTargetOrigin } from '../../services/host-target-origin';

// Messages the embedded app posts to its host, always to the exact parent origin (never '*').
type WppHostMessage =
  | { readonly type: 'wpp:navigation'; readonly url: string }
  | { readonly type: 'wpp:state'; readonly state: BookingState | null }
  | { readonly type: 'wpp:height'; readonly height: number }
  | ({ readonly type: 'wpp:event' } & HostEvent);

// A sub-action on step 3 fires several state PATCHes in a row (date, option, time, transport).
// Only the newest state is worth posting, so a burst collapses to its last value and the
// superseded ones are dropped instead of reaching the host as stale events.
const STATE_POST_DEBOUNCE_MS = 120;

@Component({
  imports: [RouterOutlet, HeaderComponent],
  selector: 'wpp-appointment',
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './appointment.html',
})
export class AppointmentPage {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly bookingState = inject(BookingStateService);
  private readonly hostEvents = inject(HostEventService);

  // Booking-state values on their way to the host; see wireStatePosts.
  private readonly stateChange$ = new Subject<BookingState | null>();

  // True inside an iframe: drives the postMessage host bridge and hides our own chrome
  // (the host provides its own). defaultView-wrapped for SSR/test safety; "not embedded" when no window.
  private readonly isIframe: boolean = ((): boolean => {
    const view = this.document.defaultView;
    return !!view && view.self !== view.top;
  })();

  protected readonly isEmbedded: boolean = this.isIframe;

  // Current in-iframe URL as state; toSignal auto-unsubscribes on destroy. Seeded with the
  // load-time URL so the host is also told the initial page (NavigationEnd alone misses it).
  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((): string | null => this.document.defaultView?.location.href ?? null),
    ),
    { initialValue: this.document.defaultView?.location.href ?? null },
  );

  public constructor() {
    // Iframe → mirror navigation + booking-state to the host via postMessage; effects tear down on
    // destroy. The web-component bridges the same data through component outputs, not postMessage.
    if (this.isIframe) {
      effect((): void => {
        const url: string | null = this.currentUrl();
        if (url) this.postToHost({ type: 'wpp:navigation', url });
      });
      // Every booking-state change, so the host can trace the funnel. null = no/reset booking; a
      // populated state carries licensePlate + mileage (PII) → exact parent origin only, as above.
      this.wireStatePosts();
      effect((): void => {
        this.stateChange$.next(this.bookingState.state());
      });
      this.observeContentHeight();
      this.hostEvents.events
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event: HostEvent): void => this.postToHost({ ...event, type: 'wpp:event' }));
    }
  }

  // Coalesces the state stream: a burst of PATCH responses posts once, with the newest value,
  // and an unchanged state posts nothing at all.
  private wireStatePosts(): void {
    this.stateChange$
      .pipe(
        debounceTime(STATE_POST_DEBOUNCE_MS),
        distinctUntilChanged(
          (previous: BookingState | null, next: BookingState | null): boolean =>
            JSON.stringify(previous) === JSON.stringify(next),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state: BookingState | null): void => {
        this.postToHost({ type: 'wpp:state', state });
      });
  }

  // Auto-height: post the body's in-flow height so hosts can grow the iframe (no nested
  // scrollbar). Body box, not documentElement.scrollHeight — that can never shrink.
  private observeContentHeight(): void {
    const body: HTMLElement | null = this.document.body;
    if (!body || typeof ResizeObserver === 'undefined') return;
    let lastHeight = 0;
    const observer: ResizeObserver = new ResizeObserver((): void => {
      const height: number = Math.ceil(body.getBoundingClientRect().height);
      if (height > 0 && height !== lastHeight) {
        lastHeight = height;
        this.postToHost({ type: 'wpp:height', height });
      }
    });
    observer.observe(body);
    this.destroyRef.onDestroy((): void => observer.disconnect());
  }

  // Post a message to the host, targeting the resolved parent origin exactly — never '*' — so PII
  // (URL or booking state) can't leak. Who may embed is gated upstream by CSP frame-ancestors.
  private postToHost(message: WppHostMessage): void {
    const view: Window | null = this.document.defaultView;
    if (!view) return;
    view.parent.postMessage(message, resolveHostTargetOrigin(view, this.document.referrer));
  }
}
